
import proj4 from 'proj4';
import { Coordinates, RoadBarrier, WinterClosedRoad, WastePoint } from "../types";
import { getCache, setCache } from "./cacheService";
import i18n from "../i18n";
import { snapBboxToGrid } from "../utils/boundsUtils";

// Definer UTM Sone 33N (EPSG:25833) for korrekt projeksjon til NVDB kartutsnitt
proj4.defs("EPSG:25833", "+proj=utm +zone=33 +ellps=GRS80 +units=m +no_defs");

// NVDB API v4 Base URLs
const NVDB_BASE_URL = "https://nvdbapiles.atlas.vegvesen.no/vegobjekter";
const NVDB_VEGNETT_URL = "https://nvdbapiles.atlas.vegvesen.no/vegnett/veglenkesekvenser/segmentert";

const OBJECT_TYPES = [39, 40]; // 39 = Rasteplass, 40 = Snuplass
const BARRIER_OBJECT_ID = 23; // Bom (fysisk sperring)
const TOLL_BOOTH_OBJECT_ID = 45; // Betalingsbom
const WINTER_CLOSED_ID = 573; // Vinterstengt strekning
const WEBCAM_OBJECT_ID = 163; // Værstasjon/Webkamera
const WASTE_CONTAINER_OBJECT_ID = 274; // Avfallsbeholder

export let lastNVDBDebug = {
  lastUrl: "",
  lastResponse: null as any,
  lastError: null as any,
  timestamp: 0
};

export interface NVDBResponse {
  objects: NVDBObject[];
  isTruncated: boolean;
  error?: string;
}

export interface NVDBObject {
  id: number;
  typeId: number;
  location: Coordinates;
  geometryPoints?: Coordinates[];
  metadata?: any;
  name?: string;
  rasteplassInfo?: {
    type: string;
    kapasitet: number;
    mobler: boolean;
    soppel: boolean;
    toalett: boolean;
    vinterstengt: boolean;
    belysning: boolean;
  };
}

const CATEGORY_MAP: Record<string, string> = {
  'E': 'Europaveg', 'R': 'Riksveg', 'F': 'Fylkesveg', 'K': 'Kommunal veg', 'P': 'Privat veg', 'S': 'Skogsbilveg'
};

export const calculateRestAreaScore = (metadata: any): number => {
  if (!metadata) return 3;
  let score = 4;
  
  // Hovedrasteplasser er generelt bedre enn underasteplasser for overnatting
  if (metadata.type?.toLowerCase().includes("hoved")) score += 2;
  
  // Fasiliteter
  if (metadata.toalett) score += 3;
  if (metadata.mobler) score += 1;
  if (metadata.soppel) score += 1;
  
  // Vinterstengt trekker kraftig ned
  if (metadata.vinterstengt) score -= 3;

  return Math.max(1, Math.min(10, score));
};

/**
 * Konverterer bounds til bbox-string for NVDB kartutsnitt-parameter.
 * Med srid=4326 i URL-en må kartutsnitt også være WGS84: minLon,minLat,maxLon,maxLat.
 * (UTM33-konvertering ga negative eastings for vestlige deler av Norge.)
 */
const getBboxString = (bounds: { latMin: number, lonMin: number, latMax: number, lonMax: number }): string => {
  return `${bounds.lonMin},${bounds.latMin},${bounds.lonMax},${bounds.latMax}`;
};

/** Snaps UTM bbox to 5km grid cells for stable cache keys */
const getCacheKeyBbox = (bounds: { latMin: number, lonMin: number, latMax: number, lonMax: number }): string => {
  try {
    const [minE, minN] = proj4("EPSG:4326", "EPSG:25833", [bounds.lonMin, bounds.latMin]);
    const [maxE, maxN] = proj4("EPSG:4326", "EPSG:25833", [bounds.lonMax, bounds.latMax]);
    const snap = (v: number) => Math.floor(v / 5000) * 5000;
    return `${snap(minE)},${snap(minN)},${snap(maxE) + 5000},${snap(maxN) + 5000}`;
  } catch (e) {
    return getBboxString(bounds);
  }
};

/**
 * For NVDB SRID 4326 er rekkefølgen i WKT-strengene: Latitude Longitude [Høyde].
 */
const parseAndConvertWKT = (wkt: string): Coordinates[] => {
  if (!wkt) return [];
  const matches = wkt.match(/-?\d+(\.\d+)?/g);
  if (!matches || matches.length < 2) return [];
  
  const points: Coordinates[] = [];
  const is3D = wkt.includes(' Z');
  const stride = is3D ? 3 : 2;

  for (let i = 0; i < matches.length; i += stride) {
    if (matches[i+1]) {
      // NVDB SRID 4326 bruker format: Lat Lon
      const lat = parseFloat(matches[i]);
      const lon = parseFloat(matches[i+1]);
      if (!isNaN(lat) && !isNaN(lon)) {
        points.push({ latitude: lat, longitude: lon });
      }
    }
  }
  return points;
};

export const fetchNVDBData = async (
  bounds: { latMin: number, lonMin: number, latMax: number, lonMax: number }
): Promise<NVDBResponse> => {
  const bboxStr = getBboxString(bounds);
  const cacheKey = `nvdb_bbox_v5_restarea_${getCacheKeyBbox(bounds)}`;
  const cached = getCache(cacheKey);
  if (cached) return { objects: cached, isTruncated: false };

  const allObjects: NVDBObject[] = [];

  try {
    const promises = OBJECT_TYPES.map(async (typeId) => {
      // VIKTIG: kartutsnitt må være UTM33, srid=4326 betyr at resultatet kommer i Lat/Lon
      const url = `${NVDB_BASE_URL}/${typeId}?kartutsnitt=${bboxStr}&srid=4326&inkluder=geometri,egenskaper&antall=1000`;
      lastNVDBDebug.lastUrl = url;
      lastNVDBDebug.timestamp = Date.now();
      
      try {
        const res = await fetch(url, {
          headers: { 
            'Accept': 'application/json', 
            'X-Client': 'Fricamp-Norway-Web' 
          }
        });

        if (!res.ok) {
          const errorText = await res.text();
          let errorData;
          try { errorData = JSON.parse(errorText); } catch(e) { errorData = errorText; }
          lastNVDBDebug.lastResponse = errorData;
          return null;
        }

        const data = await res.json();
        lastNVDBDebug.lastResponse = data;
        return { typeId, data };
      } catch (err: any) {
        lastNVDBDebug.lastError = err.message;
        return null;
      }
    });

    const results = await Promise.all(promises);
    results.forEach((result) => {
      if (!result || !result.data || !result.data.objekter) return;
      const { typeId, data } = result;
      data.objekter.forEach((obj: any) => {
        const points = parseAndConvertWKT(obj.geometri?.wkt);
        if (points.length === 0) return;
        
        let rasteplassInfo;
        const props = obj.egenskaper || [];
        
        if (typeId === 39) {
          rasteplassInfo = {
            type: props.find((e: any) => e.id === 2036)?.verdi || "Rasteplass",
            kapasitet: parseInt(props.find((e: any) => e.id === 1805)?.verdi) || 0,
            mobler: props.find((e: any) => e.id === 3793)?.verdi === "Ja",
            soppel: props.find((e: any) => e.id === 3794)?.verdi === "Ja",
            toalett: props.find((e: any) => e.id === 1101)?.verdi === "Ja",
            vinterstengt: props.find((e: any) => e.id === 9246)?.verdi === "Ja",
            belysning: props.find((e: any) => e.id === 9518)?.verdi === "Ja"
          };
        }
        
        allObjects.push({
          id: obj.id, 
          typeId: typeId, 
          location: points[0],
          geometryPoints: points.length > 1 ? points : undefined,
          metadata: props,
          name: props.find((e: any) => e.navn === 'Navn' || e.id === 1074)?.verdi || (typeId === 40 ? i18n.t('shared.rest_type_snuplass') : i18n.t('shared.rest_type_rasteplass')),
          rasteplassInfo
        });
      });
    });
    
    if (allObjects.length > 0) setCache(cacheKey, allObjects, 1000 * 60 * 15);
    return { objects: allObjects, isTruncated: false };
  } catch (error: any) {
    lastNVDBDebug.lastError = error.message;
    return { objects: [], isTruncated: false, error: error.message };
  }
};

export const fetchRoadBarriers = async (
  bounds: { latMin: number, lonMin: number, latMax: number, lonMax: number }
): Promise<RoadBarrier[]> => {
  const bboxStr = getBboxString(bounds);
  const cacheKey = `nvdb_barriers_v6_${getCacheKeyBbox(bounds)}`;
  const cached = getCache(cacheKey);
  if (cached) return cached;

  try {
    const typeIds = [BARRIER_OBJECT_ID, TOLL_BOOTH_OBJECT_ID]; // 23 + 45
    const results = await Promise.all(typeIds.map(async (typeId) => {
      const url = `${NVDB_BASE_URL}/${typeId}?kartutsnitt=${bboxStr}&srid=4326&inkluder=geometri,egenskaper,lokasjon&antall=1000`;
      const res = await fetch(url, {
        headers: {
          'Accept': 'application/json',
          'X-Client': 'Fricamp-Norway-Web'
        }
      });
      if (!res.ok) return [];
      const data = await res.json();
      if (!data.objekter) return [];

      return data.objekter.map((obj: any) => {
        const points = parseAndConvertWKT(obj.geometri?.wkt);
        if (points.length === 0) return null;

        const props = obj.egenskaper || [];
        const vegkat = obj.lokasjon?.vegsystemreferanser?.[0]?.vegsystem?.vegkategori;
        return {
          id: obj.id,
          location: points[0],
          type: typeId === TOLL_BOOTH_OBJECT_ID
            ? "Betalingsbom"
            : (props.find((e: any) => e.id === 1104)?.verdi || "Bom"),
          openingMethod: typeId === TOLL_BOOTH_OBJECT_ID
            ? "Betaling"
            : (props.find((e: any) => e.id === 9483)?.verdi || "Ukjent"),
          material: props.find((e: any) => e.id === 3842)?.verdi,
          name: typeId === TOLL_BOOTH_OBJECT_ID
            ? (props.find((e: any) => e.id === 1074)?.verdi || "Betalingsbom")
            : (props.find((e: any) => e.id === 1076)?.verdi || "Bom"),
          description: props.find((e: any) => e.id === 1077)?.verdi,
          purpose: props.find((e: any) => e.id === 1252)?.verdi,
          owner: props.find((e: any) => e.id === 7986)?.verdi || props.find((e: any) => e.id === 11993)?.verdi,
          vegkategori: vegkat,
          properties: props
        };
      }).filter(Boolean);
    }));

    const barriers: RoadBarrier[] = results.flat();
    setCache(cacheKey, barriers, 1000 * 60 * 15);
    return barriers;
  } catch (error) {
    console.error("Feil ved henting av bommer:", error);
    return [];
  }
};

import { robustOverpassQuery } from './overpassService';

const OSM_BARRIER_MAP: Record<string, string> = {
  toll_booth: 'Betalingsbom', gate: 'Grind', lift_gate: 'Løftebom',
  swing_gate: 'Svingbom', bollard: 'Pullert', block: 'Sperring',
};

export const fetchOSMBarriers = async (
  bounds: { latMin: number, lonMin: number, latMax: number, lonMax: number }
): Promise<RoadBarrier[]> => {
  const bboxStr = `${bounds.latMin},${bounds.lonMin},${bounds.latMax},${bounds.lonMax}`;
  const cacheKey = `osm_barriers_v2_${snapBboxToGrid(bounds, 0.05)}`;
  const cached = getCache(cacheKey);
  if (cached) return cached;

  const query = `[out:json][timeout:25];(node["barrier"~"toll_booth|gate|lift_gate|bollard|block"](${bboxStr}););out body;`;

  try {
    const data = await robustOverpassQuery(query);

    const barriers: RoadBarrier[] = (data.elements || [])
      .filter((e: any) => e.type === 'node' && e.lat && e.lon)
      .map((e: any) => {
        const bTag = String(e.tags?.barrier || '').toLowerCase();
        return {
          id: e.id,
          location: { latitude: e.lat, longitude: e.lon },
          type: OSM_BARRIER_MAP[bTag] || 'Bom',
          openingMethod: bTag === 'toll_booth' ? 'Betaling' : undefined,
          name: e.tags?.name || OSM_BARRIER_MAP[bTag] || 'Bom',
        };
      });

    setCache(cacheKey, barriers, 1000 * 60 * 15);
    return barriers;
  } catch {
    return [];
  }
};

// ---------------------------------------------------------------------------
// Swedish barriers (Trafikverket Väghinder via Cloud Function)
// ---------------------------------------------------------------------------
const SE_BARRIERS_URL = 'https://us-central1-fricamp-2810e.cloudfunctions.net/getSwedishBarriers';

export const fetchSwedishBarriers = async (
  bounds: { latMin: number, lonMin: number, latMax: number, lonMax: number }
): Promise<RoadBarrier[]> => {
  const cacheKey = `se_barriers_v1_${snapBboxToGrid(bounds, 0.05)}`;
  const cached = getCache(cacheKey);
  if (cached) return cached;

  try {
    const url = `${SE_BARRIERS_URL}?latMin=${bounds.latMin}&latMax=${bounds.latMax}&lonMin=${bounds.lonMin}&lonMax=${bounds.lonMax}`;
    const res = await fetch(url);
    if (!res.ok) return [];
    const data = await res.json();

    const barriers: RoadBarrier[] = (data.barriers || []).map((b: any) => ({
      id: b.id,
      location: b.location,
      type: b.type,
      openingMethod: b.openingMethod,
      material: b.material,
      name: b.name,
    }));

    setCache(cacheKey, barriers, 1000 * 60 * 15);
    return barriers;
  } catch (error) {
    console.error('Failed to fetch Swedish barriers:', error);
    return [];
  }
};

// ---------------------------------------------------------------------------
// Swedish rest areas (Trafikverket Rastplats via Cloud Function)
// ---------------------------------------------------------------------------
const SE_REST_AREAS_URL = 'https://us-central1-fricamp-2810e.cloudfunctions.net/getSwedishRestAreas';

export const fetchSwedishRestAreas = async (
  bounds: { latMin: number, lonMin: number, latMax: number, lonMax: number }
): Promise<NVDBResponse> => {
  const cacheKey = `se_rest_areas_v1_${snapBboxToGrid(bounds, 0.05)}`;
  const cached = getCache(cacheKey);
  if (cached) return { objects: cached, isTruncated: false };

  try {
    const url = `${SE_REST_AREAS_URL}?latMin=${bounds.latMin}&latMax=${bounds.latMax}&lonMin=${bounds.lonMin}&lonMax=${bounds.lonMax}`;
    const res = await fetch(url);
    if (!res.ok) return { objects: [], isTruncated: false };
    const data = await res.json();

    const objects: NVDBObject[] = (data.restAreas || []).map((r: any) => ({
      id: r.id,
      typeId: 39, // Rasteplass
      location: r.location,
      name: r.name || 'Rastplats',
      rasteplassInfo: {
        type: 'Rastplats (Trafikverket)',
        kapasitet: (r.parkingCar || 0) + (r.parkingTruck || 0),
        mobler: false, // Not in Swedish data
        soppel: false,  // Not in Swedish data
        toalett: r.latrinEmptying || false,
        vinterstengt: false,
        belysning: false,
      },
    }));

    setCache(cacheKey, objects, 1000 * 60 * 15);
    return { objects, isTruncated: false };
  } catch (error) {
    console.error('Failed to fetch Swedish rest areas:', error);
    return { objects: [], isTruncated: false };
  }
};

// ---------------------------------------------------------------------------
// Finnish rest areas (Digiroad dr_palvelu, pre-seeded to fi_rest_areas). Lovlige
// oppstillingsplasser for kjøretøy i Finland (levähdysalue/pysäköintialue/rekkaparkki).
// ---------------------------------------------------------------------------
const FI_REST_AREAS_URL = 'https://us-central1-fricamp-2810e.cloudfunctions.net/getFinnishRestAreas';

export const fetchFinnishRestAreas = async (
  bounds: { latMin: number, lonMin: number, latMax: number, lonMax: number }
): Promise<NVDBResponse> => {
  const cacheKey = `fi_rest_areas_v1_${snapBboxToGrid(bounds, 0.05)}`;
  const cached = getCache(cacheKey);
  if (cached) return { objects: cached, isTruncated: false };

  try {
    const url = `${FI_REST_AREAS_URL}?latMin=${bounds.latMin}&latMax=${bounds.latMax}&lonMin=${bounds.lonMin}&lonMax=${bounds.lonMax}`;
    const res = await fetch(url);
    if (!res.ok) return { objects: [], isTruncated: false };
    const data = await res.json();

    const objects: NVDBObject[] = (data.restAreas || []).map((r: any) => ({
      id: r.id,
      typeId: 39, // Rasteplass
      location: r.location,
      name: r.name || 'Levähdysalue',
      rasteplassInfo: {
        type: r.restType === 'levähdysalue' ? 'Levähdysalue (Digiroad)'
          : r.restType === 'rekkaparkki' ? 'Rekkaparkki (Digiroad)'
          : 'Pysäköintialue (Digiroad)',
        kapasitet: r.parkingSpaces || 0,
        mobler: false,
        soppel: false,
        toalett: false, // ikke i Digiroad-data
        vinterstengt: false,
        belysning: false,
      },
    }));

    setCache(cacheKey, objects, 1000 * 60 * 15);
    return { objects, isTruncated: false };
  } catch (error) {
    console.error('Failed to fetch Finnish rest areas:', error);
    return { objects: [], isTruncated: false };
  }
};

// ---------------------------------------------------------------------------
// Swedish traffic events (Trafikverket roadworks + accidents + trafficmessages)
// ---------------------------------------------------------------------------
const SE_TRAFFIC_EVENTS_URL = 'https://us-central1-fricamp-2810e.cloudfunctions.net/getTrafikverketTrafficEvents';

export const fetchSwedishTrafficEvents = async (
  bounds: { latMin: number, lonMin: number, latMax: number, lonMax: number }
): Promise<import('../types').TrafficEvent[]> => {
  const cacheKey = `se_traffic_events_v1_${snapBboxToGrid(bounds, 0.1)}`;
  const cached = getCache(cacheKey);
  if (cached) return cached;

  try {
    const url = `${SE_TRAFFIC_EVENTS_URL}?latMin=${bounds.latMin}&latMax=${bounds.latMax}&lonMin=${bounds.lonMin}&lonMax=${bounds.lonMax}`;
    const res = await fetch(url);
    if (!res.ok) return [];
    const data = await res.json();
    const events = (data.events || []) as import('../types').TrafficEvent[];
    setCache(cacheKey, events, 1000 * 60 * 5);
    return events;
  } catch (error) {
    console.error('Failed to fetch Swedish traffic events:', error);
    return [];
  }
};

// ---------------------------------------------------------------------------
// OSM rest areas (Denmark and other countries without specific API)
// ---------------------------------------------------------------------------
export const fetchOSMRestAreas = async (
  bounds: { latMin: number, lonMin: number, latMax: number, lonMax: number }
): Promise<NVDBResponse> => {
  const bboxStr = `${bounds.latMin},${bounds.lonMin},${bounds.latMax},${bounds.lonMax}`;
  const cacheKey = `osm_rest_areas_v1_${snapBboxToGrid(bounds, 0.05)}`;
  const cached = getCache(cacheKey);
  if (cached) return { objects: cached, isTruncated: false };

  const query = `[out:json][timeout:25];(node["highway"="rest_area"](${bboxStr});node["highway"="services"](${bboxStr});node["amenity"="parking"]["tourism"~"caravan_site|camp_site"](${bboxStr}););out body;`;

  try {
    const data = await robustOverpassQuery(query);

    const objects: NVDBObject[] = (data.elements || [])
      .filter((e: any) => e.type === 'node' && e.lat && e.lon)
      .map((e: any) => ({
        id: e.id,
        typeId: 39,
        location: { latitude: e.lat, longitude: e.lon },
        name: e.tags?.name || 'Rasteplads',
        rasteplassInfo: {
          type: 'Rasteplads (OSM)',
          kapasitet: 0,
          mobler: false,
          soppel: false,
          toalett: (e.tags?.toilets === 'yes' || e.tags?.['toilets:wheelchair'] === 'yes'),
          vinterstengt: false,
          belysning: false,
        },
      }));

    setCache(cacheKey, objects, 1000 * 60 * 15);
    return { objects, isTruncated: false };
  } catch {
    return { objects: [], isTruncated: false };
  }
};

// ---------------------------------------------------------------------------
// Danish shelters, lejrpladser & fri teltning (OSM-based)
// Returns as CampingSpot-compatible NVDBObjects with special typeId
// ---------------------------------------------------------------------------
export const fetchDanishSheltersAndCamping = async (
  bounds: { latMin: number, lonMin: number, latMax: number, lonMax: number }
): Promise<NVDBResponse> => {
  const bboxStr = `${bounds.latMin},${bounds.lonMin},${bounds.latMax},${bounds.lonMax}`;
  const cacheKey = `dk_shelters_v1_${snapBboxToGrid(bounds, 0.05)}`;
  const cached = getCache(cacheKey);
  if (cached) return { objects: cached, isTruncated: false };

  // Only camping-relevant shelters (lean_to, basic_hut) and designated primitive sites
  // Skip firepits (too much noise) and generic shelters (ambiguous)
  const query = `[out:json][timeout:30];(
    node["amenity"="shelter"]["shelter_type"~"lean_to|basic_hut|weather_shelter"](${bboxStr});
    way["amenity"="shelter"]["shelter_type"~"lean_to|basic_hut|weather_shelter"](${bboxStr});
    node["tourism"="wilderness_hut"](${bboxStr});
    node["tourism"="camp_site"]["backcountry"="yes"](${bboxStr});
    node["tourism"="camp_site"]["camp_site"="basic"](${bboxStr});
    way["tourism"="camp_site"]["backcountry"="yes"](${bboxStr});
    way["tourism"="camp_site"]["camp_site"="basic"](${bboxStr});
  );out center body;`;

  try {
    const data = await robustOverpassQuery(query);

    const objects: NVDBObject[] = (data.elements || [])
      .filter((e: any) => (e.lat && e.lon) || (e.center?.lat && e.center?.lon))
      .map((e: any) => {
        const lat = e.lat || e.center?.lat;
        const lon = e.lon || e.center?.lon;
        const tags = e.tags || {};

        // Filter out bus shelters, transit shelters, private access
        const access = (tags.access || '').toLowerCase();
        if (['private', 'no', 'customers'].includes(access)) return null;
        if (tags.amenity === 'shelter' && (tags.shelter_type === 'public_transport' || tags.public_transport)) return null;
        // Filter out commercial campsites (separate layer)
        if (tags.tourism === 'camp_site' && tags.backcountry !== 'yes' && tags.camp_site !== 'basic' && tags.tents !== 'yes') {
          if (tags.caravans === 'yes' || tags.fee === 'yes') return null;
        }

        const isShelter = tags.amenity === 'shelter' || tags.tourism === 'wilderness_hut';
        const isFirepit = tags.leisure === 'firepit';
        const isCampSite = tags.tourism === 'camp_site';

        let type = 'Lejrplads';
        if (isShelter) type = 'Shelter';
        else if (isFirepit) type = 'Bålplads';
        else if (isCampSite && tags.backcountry === 'yes') type = 'Fri teltning';
        else if (isCampSite) type = 'Primitiv lejrplads';

        return {
          id: e.id,
          typeId: 39,
          location: { latitude: lat, longitude: lon },
          name: tags.name || type,
          rasteplassInfo: {
            type,
            kapasitet: parseInt(tags.capacity) || 0,
            mobler: !!tags.bench || tags.picnic_table === 'yes',
            soppel: tags.waste_disposal === 'yes',
            toalett: tags.toilets === 'yes',
            vinterstengt: false,
            belysning: false,
          },
        };
      }).filter(Boolean);

    setCache(cacheKey, objects, 1000 * 60 * 15);
    return { objects, isTruncated: false };
  } catch {
    return { objects: [], isTruncated: false };
  }
};

export const fetchWinterClosedRoads = async (
  bounds: { latMin: number, lonMin: number, latMax: number, lonMax: number }
): Promise<WinterClosedRoad[]> => {
  const bboxStr = getBboxString(bounds);
  const cacheKey = `nvdb_winter_v5_${getCacheKeyBbox(bounds)}`;
  const cached = getCache(cacheKey);
  if (cached) return cached;

  try {
    const url = `${NVDB_BASE_URL}/${WINTER_CLOSED_ID}?kartutsnitt=${bboxStr}&srid=4326&inkluder=geometri,egenskaper&antall=500`;
    const res = await fetch(url, {
      headers: { 
        'Accept': 'application/json', 
        'X-Client': 'Fricamp-Norway-Web' 
      }
    });
    if (!res.ok) return [];
    
    const data = await res.json();
    const roads: WinterClosedRoad[] = data.objekter.map((obj: any) => {
      const geometry = parseAndConvertWKT(obj.geometri?.wkt);
      if (geometry.length === 0) return null;
      
      const props = obj.egenskaper || [];
      return {
        id: obj.id,
        name: "Vinterstengt vei", // Ofte ikke eget navn, men kan hente fra tilknyttet vegobjekt hvis behov
        geometry: geometry,
        closedFrom: props.find((e: any) => e.id === 4567)?.verdi, // Fra dato
        closedTo: props.find((e: any) => e.id === 4568)?.verdi,   // Til dato
        description: props.find((e: any) => e.id === 1077)?.verdi // Kommentar
      };
    }).filter(Boolean);

    setCache(cacheKey, roads, 1000 * 60 * 30); // 30 min cache
    return roads;
  } catch (error) {
    console.error("Feil ved henting av vinterstengte veier:", error);
    return [];
  }
};

export const fetchNVDBWasteContainers = async (
  bounds: { latMin: number; lonMin: number; latMax: number; lonMax: number }
): Promise<WastePoint[]> => {
  const bboxStr = getBboxString(bounds);
  const cacheKey = `nvdb_waste_v1_${getCacheKeyBbox(bounds)}`;
  const cached = getCache(cacheKey);
  if (cached) return cached;

  try {
    const url = `${NVDB_BASE_URL}/${WASTE_CONTAINER_OBJECT_ID}?kartutsnitt=${bboxStr}&srid=4326&inkluder=geometri,egenskaper&antall=1000`;
    const res = await fetch(url, {
      headers: { 'Accept': 'application/json', 'X-Client': 'Fricamp-Norway-Web' }
    });
    if (!res.ok) return [];
    const data = await res.json();
    if (!data.objekter) return [];

    const points: WastePoint[] = data.objekter.map((obj: any) => {
      const geometry = parseAndConvertWKT(obj.geometri?.wkt);
      if (geometry.length === 0) return null;
      const props = obj.egenskaper || [];
      return {
        id: `nvdb-waste-${obj.id}`,
        source: 'nvdb' as const,
        type: 'nvdb_container' as const,
        location: geometry[0],
        name: props.find((e: any) => e.navn === 'Navn' || e.id === 1074)?.verdi || 'Avfallsbeholder',
        description: props.find((e: any) => e.id === 1077)?.verdi,
      };
    }).filter(Boolean);

    setCache(cacheKey, points, 1000 * 60 * 15);
    return points;
  } catch (error) {
    console.error("Feil ved henting av avfallsbeholdere:", error);
    return [];
  }
};

export const fetchNearbyWebcams = async (coords: Coordinates): Promise<string | null> => {
  const radius = 0.005; 
  const bounds = {
    latMin: coords.latitude - radius,
    lonMin: coords.longitude - radius * 2,
    latMax: coords.latitude + radius,
    lonMax: coords.longitude + radius * 2
  };
  
  const bboxStr = getBboxString(bounds);
  const url = `${NVDB_BASE_URL}/${WEBCAM_OBJECT_ID}?kartutsnitt=${bboxStr}&srid=4326&inkluder=egenskaper&antall=10`;

  try {
    const res = await fetch(url, {
      headers: { 
        'Accept': 'application/json', 
        'X-Client': 'Fricamp-Norway-Web' 
      }
    });
    if (!res.ok) return null;
    const data = await res.json();
    
    if (!data.objekter || data.objekter.length === 0) return null;

    for (const obj of data.objekter) {
      const props = obj.egenskaper || [];
      const isTunnel = props.find((e: any) => e.id === 9240)?.verdi === "Ja";
      if (isTunnel) continue;

      const cameraID = props.find((e: any) => e.id === 10609)?.verdi;
      if (cameraID) {
        return `https://kamera.atlas.vegvesen.no/api/images/${cameraID}`;
      }
    }
    return null;
  } catch (error) {
    console.warn("Kamera-oppslag feilet:", error);
    return null;
  }
};

/** Enkel avstand mellom to koordinater */
const coordDist = (a: Coordinates, b: Coordinates): number =>
  Math.abs(a.latitude - b.latitude) + Math.abs(a.longitude - b.longitude);

/**
 * Sjekk om en Fricamp-plass ligger nær en skogsbilveg/privatveg.
 * Henter alle veier innen ~1.5km fra plassen, finner nærmeste S/P-vei,
 * og returnerer hele veistrekningen med node-traversering.
 */
export const fetchNearbyForestRoad = async (
  spotCoords: Coordinates
): Promise<{ roadGeometry: Coordinates[]; vegkategori: string; vegkategoriName: string } | null> => {
  const buffer = 0.012; // ~1.3km — plassen kan ligge et stykke fra veien
  const bounds = {
    lonMin: spotCoords.longitude - buffer * 2,
    latMin: spotCoords.latitude - buffer,
    lonMax: spotCoords.longitude + buffer * 2,
    latMax: spotCoords.latitude + buffer
  };
  const bboxStr = getBboxString(bounds);
  const cacheKey = `nvdb_spotroad_v1_${Math.round(spotCoords.latitude * 1000)}_${Math.round(spotCoords.longitude * 1000)}`;
  const cached = getCache(cacheKey);
  if (cached) return cached;

  try {
    // Hent ALLE veier nær plassen — filtrer til S/P etterpå
    const url = `${NVDB_VEGNETT_URL}?kartutsnitt=${bboxStr}&srid=4326&antall=2000`;
    const res = await fetch(url, {
      headers: { 'Accept': 'application/json', 'X-Client': 'Fricamp-Norway-Web' }
    });
    if (!res.ok) return null;
    const data = await res.json();
    if (!data.objekter || data.objekter.length === 0) return null;

    // Parse alle segmenter
    type Seg = { points: Coordinates[]; startNode: string; endNode: string; cat: string };
    const allSegments: Seg[] = [];
    for (const seg of data.objekter) {
      const pts = parseAndConvertWKT(seg.geometri?.wkt);
      if (pts.length < 2) continue;
      allSegments.push({
        points: pts,
        startNode: seg.startnode || '',
        endNode: seg.sluttnode || '',
        cat: seg.vegsystemreferanse?.vegsystem?.vegkategori || ''
      });
    }

    // Finn nærmeste S/P-segment til plassen
    const spSegments = allSegments.filter(s => s.cat === 'S' || s.cat === 'P');
    if (spSegments.length === 0) return null;

    let bestIdx = 0;
    let bestDist = Infinity;
    for (let i = 0; i < spSegments.length; i++) {
      for (const p of spSegments[i].points) {
        const d = coordDist(p, spotCoords);
        if (d < bestDist) { bestDist = d; bestIdx = i; }
      }
    }

    // Sjekk at veien er innen rimelig avstand (~800m)
    if (bestDist > 0.008) return null;

    // Bygg sammenhengende vei ved å følge noder fra nærmeste S/P-segment
    // Bruk ALLE segmenter (ikke bare S/P) for node-oppslag, men stopp ved annen vegkategori
    const startSeg = spSegments[bestIdx];
    const cat = startSeg.cat;

    // Finn indekser i allSegments for de valgte S/P-segmentene
    const usedNodes = new Set<string>();
    const orderedSegs: Seg[] = [startSeg];
    usedNodes.add(startSeg.startNode);
    usedNodes.add(startSeg.endNode);

    // Følg noder forover (same vegkategori)
    let endNode = startSeg.endNode;
    for (let iter = 0; iter < 200 && endNode; iter++) {
      const next = allSegments.find(s =>
        !usedNodes.has(s.startNode + s.endNode) && s.cat === cat && s.startNode === endNode
      );
      if (!next) break;
      orderedSegs.push(next);
      usedNodes.add(next.startNode + next.endNode);
      endNode = next.endNode;
    }

    // Følg noder bakover
    let startNode = startSeg.startNode;
    for (let iter = 0; iter < 200 && startNode; iter++) {
      const prev = allSegments.find(s =>
        !usedNodes.has(s.startNode + s.endNode) && s.cat === cat && s.endNode === startNode
      );
      if (!prev) break;
      orderedSegs.unshift(prev);
      usedNodes.add(prev.startNode + prev.endNode);
      startNode = prev.startNode;
    }

    // Bygg polyline
    const allPoints: Coordinates[] = [];
    for (const seg of orderedSegs) {
      if (allPoints.length > 0) {
        const last = allPoints[allPoints.length - 1];
        const segEnd = seg.points[seg.points.length - 1];
        // Reverser om siste punkt er nærmere enn første
        if (coordDist(last, segEnd) < coordDist(last, seg.points[0])) {
          seg.points.reverse();
        }
        // Hopp over duplikat grensepunkt
        if (coordDist(last, seg.points[0]) < 0.00001) {
          allPoints.push(...seg.points.slice(1));
        } else {
          allPoints.push(...seg.points);
        }
      } else {
        allPoints.push(...seg.points);
      }
    }

    if (allPoints.length < 2) return null;

    const result = { roadGeometry: allPoints, vegkategori: cat, vegkategoriName: CATEGORY_MAP[cat] || 'Veg' };
    setCache(cacheKey, result, 1000 * 60 * 10);
    return result;
  } catch {
    return null;
  }
};

export const fetchNearestPublicRoad = async (coords: Coordinates): Promise<{ location: Coordinates, category: string, categoryName: string } | null> => {
  const buffer = 0.002; 
  const bounds = {
    lonMin: coords.longitude - buffer,
    latMin: coords.latitude - buffer,
    lonMax: coords.longitude + buffer,
    latMax: coords.latitude + buffer
  };
  
  const bboxStr = getBboxString(bounds);

  try {
    const url = `${NVDB_VEGNETT_URL}?kartutsnitt=${bboxStr}&srid=4326`;
    const response = await fetch(url, { 
      headers: { 
        'Accept': 'application/json', 
        'X-Client': 'Fricamp-Norway-Web' 
      } 
    });
    if (!response.ok) return null;
    const data = await response.json();
    if (!data.objekter || data.objekter.length === 0) return null;
    const publicCategories = ['E', 'R', 'F', 'K'];
    const publicSegments = data.objekter.filter((seg: any) => 
      publicCategories.includes(seg.vegsystemreferanse?.vegsystem?.vegkategori)
    );
    if (publicSegments.length === 0) return null;
    const segment = publicSegments[0];
    const cat = segment.vegsystemreferanse?.vegsystem?.vegkategori;
    const points = parseAndConvertWKT(segment.geometri?.wkt);
    if (points.length === 0) return null;
    return { location: points[0], category: cat, categoryName: CATEGORY_MAP[cat] || 'Offentlig veg' };
  } catch (error) { return null; }
};
